<template>
  <div class="company-detail" v-if="!loading && companyName">
    <div class="header-row">
      <button class="back-button" @click="$router.push('/companies')">← Companies</button>
      <div class="header-actions">
        <button
          v-if="gmailTabAvailable"
          type="button"
          class="email-btn"
          @click="openEmailModal"
        >
          Email organization
        </button>
        <button type="button" class="add-btn" @click="openAddPeople">+ Add people</button>
      </div>
    </div>

    <div class="detail-grid">
      <section class="basic-info">
        <h1 class="name-row">
          <span
            class="star"
            :class="{ active: starred }"
            @click="toggleStar"
            :title="starred ? 'Unstar company' : 'Star company'"
          >{{ starred ? '★' : '☆' }}</span>
          {{ companyName }}
        </h1>

        <h2>Organization</h2>
        <div class="field">
          <strong>Website:</strong>
          <a
            v-if="profile.website"
            class="website-link"
            :href="formatWebsite(profile.website)"
            target="_blank"
            rel="noopener"
          >{{ profile.website }}</a>
          <span v-else>—</span>
        </div>
        <div class="field">
          <strong>Category:</strong>
          <select v-model="profile.category" class="inline-select" @change="saveProfile">
            <option value="">—</option>
            <option v-for="opt in categoryOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>
        <div class="field">
          <strong>Lead status:</strong>
          <select v-model="profile.lead_status" class="inline-select" @change="saveProfile">
            <option value="">—</option>
            <option v-for="opt in leadStatusOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="field">
          <strong>Life cycle:</strong>
          <select v-model="profile.life_cycle_status" class="inline-select" @change="saveProfile">
            <option value="">—</option>
            <option v-for="opt in lifeCycleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="field">
          <strong>People:</strong>
          {{ activeCount }} active
          <span v-if="formerCount" class="former-count">· {{ formerCount }} former</span>
        </div>
        <div class="field" v-if="(profile.tags || []).length">
          <strong>Tags:</strong>
          <span v-for="t in profile.tags" :key="t" class="pill">{{ t }}</span>
        </div>
      </section>

      <section class="people-panel">
        <div class="people-tabs">
          <button
            type="button"
            class="people-tab"
            :class="{ active: peopleTab === 'active' }"
            @click="peopleTab = 'active'"
          >
            Here now
            <span class="count">{{ activeCount }}</span>
          </button>
          <button
            type="button"
            class="people-tab tab-former"
            :class="{ active: peopleTab === 'former' }"
            @click="peopleTab = 'former'"
          >
            Former
            <span class="count">{{ formerCount }}</span>
          </button>
        </div>

        <div class="people-list">
          <div
            v-for="c in visiblePeople"
            :key="c.id"
            class="person-row"
            :class="{ former: c.company_status === 'former' }"
          >
            <div class="person-main">
              <router-link :to="`/customers/${c.id}`" class="person-name">
                {{ displayName(c) }}
              </router-link>
              <span class="person-email">{{ c.main_email || '—' }}</span>
              <span v-if="c.main_title" class="person-title">{{ c.main_title }}</span>
            </div>
            <div class="person-actions">
              <button
                v-if="c.company_status !== 'former'"
                type="button"
                class="former-btn"
                @click="setStatus(c, 'former')"
              >
                Mark former
              </button>
              <button
                v-else
                type="button"
                class="restore-btn"
                @click="setStatus(c, 'active')"
              >
                Restore
              </button>
            </div>
          </div>
          <p v-if="!visiblePeople.length" class="empty">
            {{ peopleTab === 'former' ? 'No former people marked yet.' : 'No people at this organization yet.' }}
          </p>
        </div>
      </section>

      <section class="conduit-panel">
        <div class="conduit-head">
          <h2>Conduit &amp; Estimates</h2>
          <router-link
            v-if="firstActiveContactId"
            class="mini-link"
            :to="`/estimates?customer_id=${firstActiveContactId}`"
          >Create new</router-link>
        </div>
        <p class="conduit-lede">Potential sales across everyone at this organization.</p>
        <div v-if="conduitSummary.length" class="conduit-mini-pipeline">
          <div v-for="stage in conduitSummary" :key="stage.level" class="conduit-mini-stage">
            <span class="mini-level">L{{ stage.level }}</span>
            <span class="mini-count">{{ stage.count }}</span>
            <span class="mini-total">${{ Number(stage.total || 0).toFixed(0) }}</span>
          </div>
        </div>
        <p v-if="estimatesLoading" class="empty">Loading conduit…</p>
        <p v-else-if="estimatesError" class="error">{{ estimatesError }}</p>
        <p v-else-if="!estimates.length" class="empty">No conduit opportunities for this organization yet.</p>
        <ul v-else class="conduit-list">
          <li v-for="e in estimates" :key="e.id" class="conduit-item">
            <div class="conduit-item-top">
              <span class="doc-id">#{{ e.estimate_number }} · {{ e.document_type }}</span>
              <span class="doc-total">${{ Number(e.total || 0).toFixed(2) }}</span>
            </div>
            <div class="conduit-item-meta">
              <router-link v-if="e.customer" :to="`/customers/${e.customer.id}`">
                {{ displayName(e.customer) }}
              </router-link>
              <span v-else>Unknown contact</span>
              <span v-if="e.chibi_contact_name"> · Chibi: {{ e.chibi_contact_name }}</span>
            </div>
            <div class="conduit-item-level">
              <select
                :value="e.conduit_level || 1"
                :disabled="updatingEstimateId === e.id"
                @change="updateCompanyEstimateConduit(e, $event.target.value)"
              >
                <option v-for="lvl in conduitLevels" :key="lvl.level" :value="lvl.level">
                  {{ lvl.level }}. {{ lvl.label }}
                </option>
              </select>
              <a :href="e.pdf_url" target="_blank" rel="noopener" class="pdf-link">PDF</a>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <!-- Email org modal -->
    <div v-if="showEmailModal" class="overlay" @click.self="closeEmailModal">
      <div class="modal email-modal">
        <div class="modal-header">
          <div>
            <h2>Email {{ companyName }}</h2>
            <p class="modal-sub">{{ emailSelectedCount }} selected · defaults to everyone currently here</p>
          </div>
          <button class="close-btn" @click="closeEmailModal">✕</button>
        </div>

        <div v-if="emailStep === 'pick'" class="email-pick">
          <div class="email-pick-bar">
            <button type="button" class="mini" @click="selectAllEmailable">Select all</button>
            <button type="button" class="mini ghost" @click="clearEmailable">Clear</button>
          </div>
          <div class="email-checklist">
            <label
              v-for="c in emailablePeople"
              :key="c.id"
              class="check-row"
              :class="{ checked: emailIds.has(c.id), noemail: !c.main_email }"
            >
              <input
                type="checkbox"
                :disabled="!c.main_email"
                :checked="emailIds.has(c.id)"
                @change="toggleEmailId(c.id, $event.target.checked)"
              />
              <span class="check-name">{{ displayName(c) }}</span>
              <span class="check-email">{{ c.main_email || 'no email' }}</span>
            </label>
          </div>
          <div class="modal-footer">
            <button type="button" class="cancel-btn" @click="closeEmailModal">Cancel</button>
            <button
              type="button"
              class="save-btn"
              :disabled="!emailSelectedCount"
              @click="emailStep = 'compose'"
            >
              Compose →
            </button>
          </div>
        </div>

        <div v-else class="email-compose">
          <button type="button" class="back-mini" @click="emailStep = 'pick'">← Change recipients</button>
          <p v-if="!gmailConnected" class="error">Connect Gmail from a contact’s Gmail tab first (Google login).</p>
          <label class="bcc-toggle">
            <input type="checkbox" v-model="emailUseBcc" :disabled="!gmailConnected" />
            <span>Put everyone in Bcc instead of To</span>
          </label>
          <p class="bcc-note">
            {{
              emailUseBcc
                ? 'Recipients go in Bcc (hidden from each other). Your Gmail address is used as To.'
                : `Everyone is in To (${emailSelectedCount} address${emailSelectedCount === 1 ? '' : 'es'}).`
            }}
          </p>
          <input v-model.trim="emailSubject" class="compose-input" placeholder="Subject" :disabled="!gmailConnected" />
          <div class="compose-editor" :class="{ disabled: !gmailConnected }">
            <QuillEditor
              v-model:content="emailBody"
              content-type="html"
              theme="snow"
              :options="quillOptions"
              :read-only="!gmailConnected"
              class="gmail-body-quill"
            />
          </div>
          <div class="modal-footer">
            <button type="button" class="cancel-btn" @click="closeEmailModal">Cancel</button>
            <button
              type="button"
              class="save-btn"
              :disabled="!gmailConnected || sendingEmail || !emailBody.trim()"
              @click="sendOrgEmail"
            >
              {{ sendingEmail ? 'Sending…' : `Send to ${emailSelectedCount}` }}
            </button>
          </div>
          <p v-if="emailError" class="error">{{ emailError }}</p>
          <p v-if="emailSent" class="success">Sent. Saved as email notes on each contact.</p>
        </div>
      </div>
    </div>

    <!-- Add people (reuse simple search) -->
    <div v-if="showPeopleModal" class="overlay" @click.self="closeAddPeople">
      <div class="modal">
        <div class="modal-header">
          <div>
            <h2>Add contacts</h2>
            <p class="modal-sub">Assign people to {{ companyName }}</p>
          </div>
          <button class="close-btn" @click="closeAddPeople">✕</button>
        </div>
        <input
          v-model.trim="peopleSearch"
          class="compose-input"
          placeholder="Search contacts…"
          @input="onPeopleSearch"
        />
        <div class="email-checklist">
          <label
            v-for="c in peopleResults"
            :key="c.id"
            class="check-row"
            :class="{ checked: peopleSelected.has(c.id) }"
          >
            <input
              type="checkbox"
              :checked="peopleSelected.has(c.id)"
              @change="togglePeople(c.id, $event.target.checked)"
            />
            <span class="check-name">{{ displayName(c) }}</span>
            <span class="check-email">{{ c.main_email }}</span>
          </label>
          <p v-if="peopleLoading" class="empty">Searching…</p>
          <p v-else-if="peopleSearch.length >= 2 && !peopleResults.length" class="empty">No matches.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="closeAddPeople">Cancel</button>
          <button
            type="button"
            class="save-btn"
            :disabled="!peopleSelected.size || savingPeople"
            @click="submitPeople"
          >
            {{ savingPeople ? 'Saving…' : 'Add to org' }}
          </button>
        </div>
        <p v-if="peopleError" class="error">{{ peopleError }}</p>
      </div>
    </div>
  </div>
  <p v-else-if="loading" class="loading-message">Loading organization…</p>
  <p v-else-if="error" class="error">{{ error }}</p>
</template>

<script>
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import CustomersService from '@/services/CustomersService'
import GmailService from '@/services/GmailService'
import EstimatesService, { CONDUIT_LEVELS } from '@/services/EstimatesService'
import { LIFE_CYCLE_OPTIONS, LEAD_STATUS_OPTIONS } from '@/data/contactStatuses'

function debounce(fn, ms) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), ms)
  }
}

export default {
  name: 'CompanyDetail',
  components: { QuillEditor },
  props: {
    company: { type: String, default: '' }
  },
  data() {
    return {
      loading: true,
      error: '',
      companyName: '',
      starred: false,
      profile: {
        website: '',
        category: '',
        lead_status: '',
        life_cycle_status: '',
        tags: []
      },
      activeContacts: [],
      formerContacts: [],
      peopleTab: 'active',
      showEmailModal: false,
      emailStep: 'pick',
      emailIds: new Set(),
      emailUseBcc: false,
      emailSubject: '',
      emailBody: '',
      gmailConnected: false,
      gmailEmail: '',
      sendingEmail: false,
      emailError: '',
      emailSent: false,
      quillOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link']
          ]
        },
        placeholder: 'Write your message…'
      },
      showPeopleModal: false,
      peopleSearch: '',
      peopleResults: [],
      peopleSelected: new Set(),
      peopleLoading: false,
      peopleError: '',
      savingPeople: false,
      onPeopleSearch: null,
      estimatesLoading: false,
      estimatesError: '',
      estimates: [],
      conduitSummary: [],
      conduitLevels: CONDUIT_LEVELS,
      updatingEstimateId: null,
      lifeCycleOptions: LIFE_CYCLE_OPTIONS,
      leadStatusOptions: LEAD_STATUS_OPTIONS,
      categoryOptions: [
        'Public school',
        'Private school',
        'Library',
        'Museum',
        'College/University',
        'Edu Distributor',
        'Retail distributor',
        'Craft Reseller',
        'Edu Reseller',
        'Other Reseller',
        'Non-profit',
        'Other'
      ]
    }
  },
  computed: {
    gmailTabAvailable() {
      return localStorage.getItem('gmailAvailable') === 'true'
    },
    activeCount() {
      return this.activeContacts.length
    },
    formerCount() {
      return this.formerContacts.length
    },
    visiblePeople() {
      return this.peopleTab === 'former' ? this.formerContacts : this.activeContacts
    },
    emailablePeople() {
      return this.activeContacts.filter((c) => c.main_email)
    },
    emailSelectedCount() {
      return this.emailIds.size
    },
    firstActiveContactId() {
      return this.activeContacts[0]?.id || null
    }
  },
  watch: {
    company: {
      immediate: true,
      handler() {
        this.load()
      }
    },
    '$route.query.name'() {
      this.load()
    }
  },
  created() {
    this.onPeopleSearch = debounce(this.fetchPeople, 280)
    if (this.gmailTabAvailable) this.loadGmailStatus()
  },
  methods: {
    displayName(c) {
      return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.main_email || `#${c.id}`
    },
    formatWebsite(url) {
      if (!url) return '#'
      return /^https?:\/\//i.test(url) ? url : `https://${url}`
    },
    async load() {
      const name = (this.company || this.$route.query.name || '').toString().trim()
      if (!name) {
        this.error = 'Missing organization name.'
        this.loading = false
        this.companyName = ''
        return
      }
      this.loading = true
      this.error = ''
      try {
        const { data } = await CustomersService.getCompany(name)
        this.companyName = data.company
        this.starred = !!data.starred
        this.profile = {
          website: data.profile?.website || '',
          category: data.profile?.category || '',
          lead_status: data.profile?.lead_status || '',
          life_cycle_status: data.profile?.life_cycle_status || '',
          tags: data.profile?.tags || []
        }
        this.activeContacts = data.active_contacts || []
        this.formerContacts = data.former_contacts || []
        await this.loadEstimates()
      } catch (e) {
        console.error(e)
        this.error = 'Could not load organization.'
        this.companyName = ''
      } finally {
        this.loading = false
      }
    },
    async loadEstimates() {
      if (!this.companyName) {
        this.estimates = []
        this.conduitSummary = []
        return
      }
      this.estimatesLoading = true
      this.estimatesError = ''
      try {
        const { data } = await EstimatesService.listByCompany(this.companyName)
        this.estimates = data.estimates || []
        this.conduitSummary = Array.isArray(data.conduit_summary) ? data.conduit_summary : []
        if (Array.isArray(data.conduit_levels) && data.conduit_levels.length) {
          this.conduitLevels = data.conduit_levels
        }
      } catch {
        this.estimatesError = 'Could not load conduit for this organization.'
        this.estimates = []
      } finally {
        this.estimatesLoading = false
      }
    },
    async updateCompanyEstimateConduit(e, level) {
      const next = Number(level)
      if (!e?.id || !e?.customer_id || !next || next === Number(e.conduit_level)) return
      this.updatingEstimateId = e.id
      try {
        const { data } = await EstimatesService.update(e.customer_id, e.id, { conduit_level: next })
        const idx = this.estimates.findIndex((x) => x.id === e.id)
        if (idx >= 0) this.estimates.splice(idx, 1, { ...this.estimates[idx], ...data })
        this.conduitSummary = this.conduitLevels.map((lvl) => {
          const matches = this.estimates.filter((row) => Number(row.conduit_level || 1) === lvl.level)
          return {
            ...lvl,
            count: matches.length,
            total: matches.reduce((acc, row) => acc + Number(row.total || 0), 0)
          }
        })
      } catch {
        alert('Could not update conduit level.')
      } finally {
        this.updatingEstimateId = null
      }
    },
    async toggleStar() {
      try {
        const { data } = await CustomersService.toggleCompanyStar(this.companyName)
        this.starred = !!data.starred
      } catch {
        alert('Could not update star.')
      }
    },
    async saveProfile() {
      try {
        await CustomersService.updateCompanyProfile(this.companyName, {
          category: this.profile.category,
          lead_status: this.profile.lead_status,
          life_cycle_status: this.profile.life_cycle_status
        })
      } catch {
        alert('Could not save organization profile.')
      }
    },
    async setStatus(c, status) {
      try {
        await CustomersService.updateCompanyContactStatus(this.companyName, c.id, status)
        c.company_status = status
        if (status === 'former') {
          this.activeContacts = this.activeContacts.filter((x) => x.id !== c.id)
          this.formerContacts = [c, ...this.formerContacts]
        } else {
          this.formerContacts = this.formerContacts.filter((x) => x.id !== c.id)
          this.activeContacts = [...this.activeContacts, c].sort((a, b) =>
            this.displayName(a).localeCompare(this.displayName(b))
          )
        }
      } catch (e) {
        console.error(e)
        alert('Could not update status. Run DB migrations if this is a new feature.')
      }
    },
    openEmailModal() {
      this.emailStep = 'pick'
      this.emailSubject = ''
      this.emailBody = ''
      this.emailUseBcc = false
      this.emailError = ''
      this.emailSent = false
      this.emailIds = new Set(
        this.emailablePeople.map((c) => c.id)
      )
      this.showEmailModal = true
      this.loadGmailStatus()
    },
    closeEmailModal() {
      this.showEmailModal = false
    },
    selectAllEmailable() {
      this.emailIds = new Set(this.emailablePeople.map((c) => c.id))
    },
    clearEmailable() {
      this.emailIds = new Set()
    },
    toggleEmailId(id, checked) {
      const next = new Set(this.emailIds)
      if (checked) next.add(id)
      else next.delete(id)
      this.emailIds = next
    },
    loadGmailStatus() {
      GmailService.status()
        .then(({ data }) => {
          this.gmailConnected = !!data.connected
          this.gmailEmail = data.email || ''
        })
        .catch(() => {
          this.gmailConnected = false
          this.gmailEmail = ''
        })
    },
    async sendOrgEmail() {
      const selected = this.activeContacts.filter(
        (c) => this.emailIds.has(c.id) && c.main_email
      )
      const recipients = selected.map((c) => c.main_email)
      const customerIds = selected.map((c) => c.id)
      if (!recipients.length) return
      this.sendingEmail = true
      this.emailError = ''
      this.emailSent = false
      try {
        let payload
        if (this.emailUseBcc) {
          const selfTo = this.gmailEmail || recipients[0]
          payload = {
            to: selfTo,
            bcc: recipients.join(', '),
            subject: this.emailSubject,
            body: this.emailBody,
            html: true,
            customer_ids: customerIds
          }
        } else {
          payload = {
            to: recipients.join(', '),
            subject: this.emailSubject,
            body: this.emailBody,
            html: true,
            customer_ids: customerIds
          }
        }
        await GmailService.sendEmail(payload)
        this.emailSent = true
      } catch (e) {
        console.error(e)
        this.emailError = 'Could not send email.'
      } finally {
        this.sendingEmail = false
      }
    },
    openAddPeople() {
      this.peopleSearch = ''
      this.peopleResults = []
      this.peopleSelected = new Set()
      this.peopleError = ''
      this.showPeopleModal = true
    },
    closeAddPeople() {
      this.showPeopleModal = false
    },
    togglePeople(id, checked) {
      const next = new Set(this.peopleSelected)
      if (checked) next.add(id)
      else next.delete(id)
      this.peopleSelected = next
    },
    async fetchPeople() {
      const q = this.peopleSearch.trim()
      if (q.length < 2) {
        this.peopleResults = []
        return
      }
      this.peopleLoading = true
      try {
        const { data } = await CustomersService.list({ q, page: 1, per_page: 40 })
        const existing = new Set([
          ...this.activeContacts.map((c) => c.id),
          ...this.formerContacts.map((c) => c.id)
        ])
        this.peopleResults = (data.customers || []).filter((c) => !existing.has(c.id))
      } catch {
        this.peopleResults = []
      } finally {
        this.peopleLoading = false
      }
    },
    async submitPeople() {
      this.savingPeople = true
      this.peopleError = ''
      try {
        await CustomersService.addCompanyContacts(
          this.companyName,
          Array.from(this.peopleSelected)
        )
        this.closeAddPeople()
        await this.load()
      } catch {
        this.peopleError = 'Could not add contacts.'
      } finally {
        this.savingPeople = false
      }
    }
  }
}
</script>

<style scoped>
.company-detail { padding: 0.5rem 0.75rem 2rem; color: #fff; }
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.back-button, .email-btn, .add-btn {
  border: none;
  border-radius: 6px;
  padding: 0.45rem 0.85rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
}
.back-button { background: #4a5568; }
.email-btn { background: #3182ce; }
.add-btn { background: #38a169; }
.header-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.detail-grid {
  display: grid;
  grid-template-columns: minmax(240px, 320px) 1fr;
  gap: 1.25rem;
}
@media (max-width: 860px) {
  .detail-grid { grid-template-columns: 1fr; }
}
.basic-info, .people-panel {
  background: linear-gradient(165deg, rgba(30,30,47,.95), rgba(21,21,37,.98));
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
}
.name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  font-size: 1.55rem;
}
.star {
  cursor: pointer;
  color: rgba(255,255,255,.45);
  font-size: 1.4rem;
}
.star.active { color: #f6e05e; }
.basic-info h2 {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  color: rgba(255,255,255,.65);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.field {
  margin-bottom: 0.7rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.6rem;
  align-items: center;
}
.field strong { color: rgba(255,255,255,.7); min-width: 5.5rem; }
.inline-select {
  background: rgba(0,0,0,.35);
  border: 1px solid rgba(255,255,255,.25);
  color: #fff;
  border-radius: 5px;
  padding: 0.25rem 0.4rem;
}
.website-link { color: #90cdf4; }
.former-count { color: #fda4af; }
.pill {
  display: inline-block;
  margin: 0 0.25rem 0.2rem 0;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.75rem;
  background: rgba(26,188,156,.15);
  border: 1px solid rgba(26,188,156,.4);
  color: #a7f3d0;
}
.people-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255,255,255,.1);
  margin: -0.25rem -1.25rem 0.75rem;
  padding: 0 0.5rem;
}
.people-tab {
  flex: 1;
  background: transparent;
  border: none;
  color: rgba(255,255,255,.55);
  padding: 0.7rem;
  cursor: pointer;
  font-weight: 600;
  border-bottom: 2px solid transparent;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.35rem;
}
.people-tab.active {
  color: #6ee7b7;
  border-bottom-color: #1abc9c;
}
.people-tab.tab-former.active {
  color: #fda4af;
  border-bottom-color: #dc143c;
}
.people-tab .count {
  font-size: 0.75rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: rgba(255,255,255,.1);
}
.person-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.35rem;
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.person-row.former { opacity: 0.78; }
.person-main { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.person-name { color: #fbd38d; text-decoration: none; font-weight: 700; }
.person-name:hover { text-decoration: underline; color: #fff; }
.person-email, .person-title { color: rgba(255,255,255,.65); font-size: 0.85rem; }
.former-btn, .restore-btn {
  border: none;
  border-radius: 5px;
  padding: 0.35rem 0.6rem;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
  white-space: nowrap;
}
.former-btn { background: rgba(220, 20, 60, 0.75); }
.restore-btn { background: #38a169; }
.empty { color: rgba(255,255,255,.55); font-style: italic; padding: 1rem 0; }
.loading-message { color: #ffa; font-style: italic; margin: 1rem; }
.error { color: #feb2b2; }
.success { color: #a7f3d0; }
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 1rem;
}
.modal {
  width: 100%;
  max-width: 640px;
  background: linear-gradient(165deg, #1e1e2f, #151525);
  border: 1px solid rgba(167, 139, 250, 0.35);
  border-radius: 12px;
  padding: 1.2rem 1.35rem;
  color: #fff;
  max-height: 88vh;
  overflow-y: auto;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}
.modal-header h2 { margin: 0; font-size: 1.15rem; }
.modal-sub { margin: 0.25rem 0 0; color: rgba(255,255,255,.65); font-size: 0.85rem; }
.close-btn { background: none; border: none; color: #fff; cursor: pointer; font-size: 1.1rem; }
.email-pick-bar { display: flex; gap: 0.4rem; margin-bottom: 0.5rem; }
.mini {
  background: #2d3748;
  border: none;
  color: #fff;
  border-radius: 5px;
  padding: 0.3rem 0.6rem;
  cursor: pointer;
  font-weight: 600;
}
.mini.ghost { background: transparent; border: 1px solid rgba(255,255,255,.25); }
.email-checklist {
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 8px;
  max-height: 40vh;
  overflow-y: auto;
  margin-bottom: 0.75rem;
}
.check-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.45rem 0.7rem;
  border-bottom: 1px solid rgba(255,255,255,.06);
  cursor: pointer;
}
.check-row.checked { background: rgba(49, 130, 206, 0.15); }
.check-row.noemail { opacity: 0.45; }
.check-name { font-weight: 600; }
.check-email { color: #cbd5e0; font-size: 0.85rem; }
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.cancel-btn { background: #4a5568; border: none; border-radius: 6px; padding: 0.4rem 0.75rem; color: #fff; font-weight: 600; cursor: pointer; }
.save-btn { background: #38a169; border: none; border-radius: 6px; padding: 0.4rem 0.75rem; color: #fff; font-weight: 700; cursor: pointer; }
.save-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.conduit-panel {
  grid-column: 1 / -1;
  margin-top: 0.5rem;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
}
.conduit-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}
.conduit-head h2 {
  margin: 0;
  font-size: 1.15rem;
  color: #fbbf24;
}
.conduit-lede {
  margin: 0.35rem 0 0.75rem;
  color: #a0aec0;
  font-size: 0.9rem;
}
.mini-link {
  color: #90cdf4;
  font-weight: 600;
  text-decoration: none;
}
.conduit-mini-pipeline {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.4rem;
  margin-bottom: 0.85rem;
}
.conduit-mini-stage {
  padding: 0.45rem 0.5rem;
  border-radius: 10px;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
}
.mini-level { display: block; font-size: 0.7rem; color: #7dd3fc; font-weight: 700; }
.mini-count { display: block; font-size: 1.05rem; font-weight: 700; }
.mini-total { display: block; font-size: 0.75rem; color: #a7f3d0; }
.conduit-list { list-style: none; padding: 0; margin: 0; }
.conduit-item {
  padding: 0.7rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.conduit-item-top {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-weight: 700;
}
.conduit-item-meta {
  margin-top: 0.25rem;
  font-size: 0.88rem;
  color: #cbd5e0;
}
.conduit-item-meta a { color: #90cdf4; }
.conduit-item-level {
  margin-top: 0.4rem;
  display: flex;
  gap: 0.6rem;
  align-items: center;
}
.conduit-item-level select {
  flex: 1;
  max-width: 320px;
  padding: 0.3rem 0.45rem;
  border-radius: 6px;
  border: 1px solid #555;
  background: #2d3040;
  color: #fff;
}
.pdf-link { color: #fbd38d; font-weight: 600; }
@media (max-width: 800px) {
  .conduit-mini-pipeline { grid-template-columns: 1fr 1fr; }
}

.compose-input {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 0.55rem;
  background: rgba(0,0,0,.35);
  border: 1px solid rgba(255,255,255,.25);
  color: #fff;
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
}
.compose-editor { margin-bottom: 0.5rem; }
.compose-editor.disabled { opacity: 0.5; pointer-events: none; }
.gmail-body-quill :deep(.ql-toolbar.ql-snow) {
  background: rgba(255,255,255,.06);
  border-color: rgba(255,255,255,.2);
}
.gmail-body-quill :deep(.ql-container.ql-snow) {
  border-color: rgba(255,255,255,.2);
  min-height: 140px;
  color: #fff;
}
.gmail-body-quill :deep(.ql-editor.ql-blank::before) { color: rgba(255,255,255,.4); }
.bcc-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.45rem;
  cursor: pointer;
  font-weight: 600;
  color: #e9d5ff;
}
.bcc-toggle input {
  width: 1rem;
  height: 1rem;
  accent-color: #a855f7;
}
.bcc-note { font-size: 0.8rem; color: rgba(255,255,255,.55); margin: 0 0 0.65rem; }
.back-mini {
  background: none;
  border: none;
  color: #90cdf4;
  cursor: pointer;
  margin-bottom: 0.6rem;
  padding: 0;
  font-weight: 600;
}
</style>
